import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
} from '@angular/forms';

import { deCapitalize } from '../utils/string.utils';
import { FieldErrorModel } from './field-error.model';

export interface AllValidationErrors {
  controlName: string;
  errorName: string;
  errorValue: any;
}

export abstract class ModifyForm extends UntypedFormGroup {
  protected backendErrorMap: { [key: string]: string } = {};

  setBackendErrors(fieldErrors: FieldErrorModel[]): void {
    this.clearValidators();
    if (fieldErrors && fieldErrors instanceof Array) {
      fieldErrors.forEach((error) => {
        const fieldName = deCapitalize(error.fieldName);
        const controlFieldName = this.backendErrorMap[fieldName];
        const control =
          this.controls[controlFieldName] || this.controls[fieldName];

        if (control) {
          control.setErrors({ backendError: error.message });
          control.markAsDirty();
          control.markAsTouched();
        }
      });
      this.updateValueAndValidity();
    }
  }

  getFormValidationErrors(
    controls?: {
      [key: string]: AbstractControl;
    },
    parentKey?: string
  ): AllValidationErrors[] {
    let errors: AllValidationErrors[] = [];
    const localControls = controls || this.controls;
    Object.keys(localControls).forEach((key) => {
      const control = localControls[key];
      const fullKey = parentKey ? `${parentKey}.${key}` : key;

      if (control instanceof UntypedFormGroup) {
        errors = errors.concat(
          this.getFormValidationErrors(control.controls, key)
        );
      }

      if (control instanceof UntypedFormArray) {
        control.controls.forEach((item, index) => {
          if (item instanceof UntypedFormGroup) {
            errors = errors.concat(
              this.getFormValidationErrors(
                item.controls,
                `${fullKey}[${index}]`
              )
            );
          }
        });
      }

      errors = errors.concat(this.getControlErrors(control, fullKey));
    });
    return errors;
  }

  updateValueAndValidityAll(control?: AbstractControl): void {
    if (control instanceof UntypedFormArray) {
      control.controls.forEach((item) => this.updateValueAndValidityAll(item));
    }

    if (control instanceof UntypedFormGroup) {
      Object.values(control.controls).forEach((item) => {
        this.updateValueAndValidityAll(item);
      });
    }

    if (control) {
      control.updateValueAndValidity();
    } else {
      this.updateValueAndValidityAll(this);
    }
  }

  private getFormArrayErrors(
    control: UntypedFormArray,
    key: string
  ): AllValidationErrors[] {
    let errors: AllValidationErrors[] = [];

    control.controls.forEach((item, index) => {
      const fullKey = `${key}[${index}]`;

      if (item instanceof UntypedFormGroup) {
        errors = errors.concat(
          this.getFormValidationErrors(item.controls, fullKey)
        );
      }

      if (item instanceof UntypedFormArray) {
        errors = errors.concat(this.getFormArrayErrors(item, fullKey));
      }

      if (item instanceof UntypedFormControl) {
        errors = errors.concat(this.getControlErrors(item, fullKey));
      }
    });
    return errors;
  }

  private getControlErrors(
    control: AbstractControl,
    key: string
  ): AllValidationErrors[] {
    const errors: AllValidationErrors[] = [];
    const controlErrors: ValidationErrors = control.errors;

    if (controlErrors !== null) {
      Object.keys(controlErrors).forEach((keyError) => {
        errors.push({
          controlName: key,
          errorName: keyError,
          errorValue: controlErrors[keyError],
        });
      });
    }

    return errors;
  }

  protected getBackendError(control: UntypedFormControl): string {
    return control.invalid && control.hasError('backendError')
      ? control.errors.backendError
      : '';
  }

  protected getRequiredError(control: UntypedFormControl): string {
    return control.invalid && control.hasError('required')
      ? 'VALIDATION.field_required'
      : '';
  }

  protected getMaxError(control: UntypedFormControl): string {
    return control.invalid && control.hasError('max') ? 'VALIDATION.max' : '';
  }

  protected getMinError(control: UntypedFormControl): string {
    return control.invalid && control.hasError('min') ? 'VALIDATION.min' : '';
  }

  protected getPatternError(
    control: UntypedFormControl,
    message: string
  ): string {
    return control.invalid && control.hasError('pattern') ? message : '';
  }

  protected getMaxErrorParams(control: UntypedFormControl): string {
    return control.invalid && control.getError('max');
  }

  protected getMinErrorParams(control: UntypedFormControl): string {
    return control.invalid && control.getError('min');
  }
}
